import React, { SyntheticEvent, useState } from "react";

// Internal Imports
import {
    AdditionalTestsFormGridContainer,
    AdditionalTestsFormContainer,
    AdditionalTestsFormGridItemBy6,
    AdditionalTestsFormLabel,
    AdditionalTestsFormFieldContainer,
    AdditionalTestsFormCheckboxContainer,
    GroupTitle,
} from "./OrderAdditionalTestsForm.styles";
import SelectBox, { SelectOption } from "../../../../../components/core/select-box/SelectBox";
import TextArea from "../../../../../components/core/input-form/TextArea";
import ClearSubmitFormButtons from "../../../../../components/core/clear-submit-form-buttons/ClearSubmitFormButtons";
import { AdditionalTestTranslations } from "../../translations/types";

// Professional dropdown options
const PROFESSIONAL_OPTIONS = [
    { value: "Physio", label: "Physio" },
    { value: "Nurse", label: "Nurse" },
];

// Test groups
const TEST_GROUPS = {
    mobility: "mobilityCheckbox",
    // adlAssessment: "adlRelatedAssessment",
    clinicalPrediction: "clinicalPredictionCheckbox"
};

interface OrderAdditionalTestsFormProps {
    onSubmit: (professional: string, assessments: any, reason: string, summary: string) => void;
    translations: AdditionalTestTranslations;
    language: string;
}

export const OrderAdditionalTestsForm: React.FC<OrderAdditionalTestsFormProps> = ({ onSubmit, translations, language }) => {
    const [professional, setProfessional] = useState<SelectOption>(PROFESSIONAL_OPTIONS[0]);
    const [reason, setReason] = useState<string>("");
    const [summary, setSummary] = useState<string>("");
    const [assessments, setAssessments] = useState<any>(
        Object.keys(TEST_GROUPS).reduce((acc, key) => ({ ...acc, [key]: {} }), {})
    );

    const handleCheckboxChange = (group: string, name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setAssessments((prevState: any) => ({
            ...prevState,
            [group]: {
                ...prevState[group],
                [name]: e.target.checked,
            },
        }));
    };

    const clearForm = () => {
        setProfessional(PROFESSIONAL_OPTIONS[0]);
        setReason("");
        setSummary("");
        setAssessments(Object.keys(TEST_GROUPS).reduce((acc, key) => ({ ...acc, [key]: {} }), {}));
    };

    const handleFormSubmit = async () => {
        const reasonValue = language === "en" ? reason.trim() : "XXXXXXXXXX";
        const summaryValue = language === "en" ? summary.trim() : "XXXXXXXXXX";

        if (professional && reasonValue.length >= 5 && summaryValue.length >= 5) {
            const selectedAssessments = Object.entries(assessments)
                .flatMap(([_, tests]) =>
                    //@ts-ignore
                    Object.entries(tests)
                        .filter(([_, isChecked]) => isChecked)
                        .map(([name]) => name)
                );

            await onSubmit(language === "en" ? professional.value : "Physio", selectedAssessments, reasonValue, summaryValue);
        }
    };

    const isNurse = professional?.value === "Nurse";

    return (
        <form onSubmit={(e: SyntheticEvent) => e.preventDefault()}>
            <AdditionalTestsFormContainer>
                <AdditionalTestsFormGridContainer>
                    {language === "en" && (
                        <AdditionalTestsFormGridItemBy6>
                            <AdditionalTestsFormLabel htmlFor="professional">{translations.professional}</AdditionalTestsFormLabel>
                            <AdditionalTestsFormFieldContainer>
                                <SelectBox
                                    value={[professional || PROFESSIONAL_OPTIONS[0]]}
                                    options={PROFESSIONAL_OPTIONS}
                                    onChange={(value: SelectOption) => setProfessional(value)}
                                />
                            </AdditionalTestsFormFieldContainer>
                        </AdditionalTestsFormGridItemBy6>
                    )}

                    {Object.entries(TEST_GROUPS).map(([group, tests]) => (
                        !isNurse && (
                            <AdditionalTestsFormGridItemBy6 key={group}>
                                {/* @ts-ignore */}
                                <GroupTitle>{translations[group]}</GroupTitle>
                                <AdditionalTestsFormCheckboxContainer>
                                    {/* @ts-ignore */}
                                    {translations[tests].map((test) => (
                                        <label key={test.name}>
                                            <input
                                                type="checkbox"
                                                name={test.name}
                                                checked={assessments[group][test.name] || false}
                                                onChange={handleCheckboxChange(group, test.name)}
                                            />
                                            {test.label}
                                        </label>
                                    ))}
                                </AdditionalTestsFormCheckboxContainer>
                            </AdditionalTestsFormGridItemBy6>
                        )
                    ))}

                    {language === "en" && (
                        <>
                            <AdditionalTestsFormGridItemBy6>
                                <AdditionalTestsFormLabel htmlFor="reason">{translations.reasonReferral}:</AdditionalTestsFormLabel>
                                <AdditionalTestsFormFieldContainer>
                                    {/* @ts-ignore */}
                                    <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
                                </AdditionalTestsFormFieldContainer>
                            </AdditionalTestsFormGridItemBy6>

                            <AdditionalTestsFormGridItemBy6>
                                <AdditionalTestsFormLabel htmlFor="summary">{translations.summary}:</AdditionalTestsFormLabel>
                                <AdditionalTestsFormFieldContainer>
                                    {/* @ts-ignore */}
                                    <TextArea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
                                </AdditionalTestsFormFieldContainer>
                            </AdditionalTestsFormGridItemBy6>
                        </>
                    )}
                </AdditionalTestsFormGridContainer>
            </AdditionalTestsFormContainer>

            <ClearSubmitFormButtons
                submitText={translations.submitButton}
                clearText={translations.clearButton}
                isSubmitDisabled={
                    !professional ||
                    (language === "en" && (reason.trim().length < 5 || summary.trim().length < 5))
                }
                onSubmit={handleFormSubmit}
                onClear={clearForm}
                RenderComponent={() => <></>}
            />
        </form>
    );
};
export default OrderAdditionalTestsForm;
